import { getDevice, UUIDs } from '../ble';
import protobuf from 'protobufjs';

const AUTO_UPDATE_TIMEOUT = 1000;
const RETRY_COUNT = 3;
const MIN_RETRY_DELAY = 200;
const MAX_RETRY_DELAY = 700;

class ProtobufProtocolService {
  constructor() {
    this.protobufRoot = null;
    this.isConnected = false;
    this.connectionLost = false;
    this.protobufSchema = null;
    this.protobufCharacteristics = [];
    this.protobufSchemaRoot = null;
    this.protobufSchemaProtocol = null;
    this.device = null;
    this.server = null;
    this.protobufService = null;
    this.characteristicUpdateTimeouts = new Map();
    this.notificationListeners = new Map();
    this.characteristicCopies = new Map();
    this.eventListeners = new Map();
    this._writeStatusQueue = [];
    this._isProcessingWriteStatus = false;
  }

  // Initialize the service
  async initialize() {
    if (!this.protobufRoot) {
      this.protobufRoot = await protobuf.load('/protobufSchema.proto');
    }
  }

  // Connection management
  async connect() {
    try {
      this.isConnected = true;
      this.connectionLost = false;
      this.protobufSchema = null;
      this.protobufCharacteristics = [];
      // GATT objects do not survive a reconnect, so never carry the cache across one.
      this.characteristicCopies.clear();

      this.device = getDevice();
      this.server = await this.device.gatt.connect();
      
      // Get the protobuf service
      this.protobufService = await this.server.getPrimaryService(UUIDs.protobufService);
      
      // Read the protbufSchemaCharacteristic
      const protbufSchemaCharacteristic = await this.protobufService.getCharacteristic(UUIDs.protbufSchemaCharacteristic);
      const schemaValue = await protbufSchemaCharacteristic.readValue();
      
      // Decode the schema using protobuf
      const messageType = this.protobufRoot.lookupType('ProtobufService');
      this.protobufSchema = messageType.decode(new Uint8Array(schemaValue.buffer));

      // Load the protobuf schema root
      this.protobufSchemaRoot = await protobuf.load(this.protobufSchema.schema);
      const namespaceNames = Object.keys(this.protobufSchemaRoot.nested);

      // Also load the protobufSchema.proto for collection protocol types
      this.protobufSchemaProtocol = await protobuf.load('/protobufSchema.proto');

      const characteristicsArray = [];

      // Step 1: Identify and process collection characteristics
      const collectionCharacteristics = [];
      const regularCharacteristics = [];

      for (const characteristic of this.protobufSchema.characteristics) {
        const isCollection = this.isCollectionCharacteristic(characteristic);
        
        if (isCollection) {
          collectionCharacteristics.push(characteristic);
        } else {
          regularCharacteristics.push(characteristic);
        }
      }

      // Step 2: Process regular characteristics
      for (const characteristic of regularCharacteristics) {
        const processedChar = await this.processRegularCharacteristic(
          characteristic, 
          this.protobufSchemaRoot, 
          namespaceNames
        );
        if (processedChar) {
          characteristicsArray.push(processedChar);
        }
      }

      // Step 3: Process collection characteristics
      for (const characteristic of collectionCharacteristics) {
        const processedCollection = await this.processCollectionCharacteristic(characteristic);
        if (processedCollection) {
          characteristicsArray.push(processedCollection);
        }
      }

      // Update the characteristics array
      this.protobufCharacteristics = characteristicsArray;

      // Emit events
      this.emit('connectionStateChanged');
      this.emit('schemaUpdated');
      this.emit('characteristicsUpdated');

      return {
        success: true,
        schema: this.protobufSchema,
        characteristics: this.protobufCharacteristics
      };
    } catch (error) {
      console.error('Connection failed:', error);
      this.isConnected = false;
      this.connectionLost = true;
      
      // Emit connection state change
      this.emit('connectionStateChanged');
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  disconnect() {
    this.isConnected = false;
    this.connectionLost = true;
    
    // Clear all timeouts
    this.characteristicUpdateTimeouts.forEach(timeout => clearTimeout(timeout));
    this.characteristicUpdateTimeouts.clear();
    
    // Remove all notification listeners
    this.notificationListeners.forEach((listener, characteristic) => {
      if (characteristic && characteristic.removeEventListener) {
        characteristic.removeEventListener('characteristicvaluechanged', listener);
      }
    });
    this.notificationListeners.clear();
    this.characteristicCopies.clear();

    // Emit connection state change
    this.emit('connectionStateChanged');
  }

  // Helper function to determine if a characteristic is a collection
  isCollectionCharacteristic(characteristic) {
    const isCollectionByFlag = characteristic.collection === true;
    const isCollectionByType = characteristic.protobufType === 'ProtobufCollectionStatus' || 
                               characteristic.protobufType === 'ProtobufCollectionRecord';
    
    return isCollectionByFlag || isCollectionByType;
  }

  // Helper function to process regular characteristics
  async processRegularCharacteristic(characteristic, protobufSchemaRoot, namespaceNames) {
    // Try to find the message type in any namespace
    let messageType = null;
    for (const namespace of namespaceNames) {
      try {
        const fullTypeName = `${namespace}.${characteristic.protobufType}`;
        messageType = protobufSchemaRoot.lookupType(fullTypeName);
        if (messageType) {
          break;
        }
      } catch (e) {
        // Type not found in this namespace, continue searching
        continue;
      }
    }
    
    // If not found in any namespace, try without namespace
    if (!messageType) {
      try {
        messageType = protobufSchemaRoot.lookupType(characteristic.protobufType);
      } catch (e) {
        console.error(`Could not find type ${characteristic.protobufType} in any namespace`);
        return null;
      }
    }

    const BLEcharacteristic = await this.protobufService.getCharacteristic(characteristic.uuid);
    let value;

    if (BLEcharacteristic.properties.read) {
      try {
        const rawValue = await BLEcharacteristic.readValue();
        value = messageType.decode(new Uint8Array(rawValue.buffer));
      } catch (error) {
        console.error("Error reading value, probably not defined yet", error);
        value = messageType.create();
      }
    } else {
      // Initialize with default values
      value = messageType.create();
    }

    if (BLEcharacteristic.properties.notify) {
      await BLEcharacteristic.startNotifications();
      const listener = (event) => {
        const rawValue = event.target.value;
        const newValue = messageType.decode(new Uint8Array(rawValue.buffer));

        // Update the characteristic value in the state
        const charIndex = this.protobufCharacteristics.findIndex(c => c.uuid === characteristic.uuid);
        if (charIndex !== -1) {
          this.protobufCharacteristics[charIndex].value = newValue;
          // Emit update event
          this.emit('characteristicUpdated', {
            uuid: characteristic.uuid,
            value: newValue,
            index: charIndex
          });
        }
      };
      
      BLEcharacteristic.addEventListener('characteristicvaluechanged', listener);
      this.notificationListeners.set(BLEcharacteristic, listener);
    }

    // Create a new object for each characteristic
    const processedChar = {
      uuid: characteristic.uuid,
      protobufType: characteristic.protobufType,
      messageType,
      BLEcharacteristic,
      value,
      writable: characteristic.writable,
      autoUpdate: true,
      hasChanges: false,
      isCollection: false
    };

    return processedChar;
  }

  // Helper function to process collection characteristics
  async processCollectionCharacteristic(characteristic) {
    // Handle different collection characteristic types
    if (characteristic.protobufType === 'ProtobufCollectionStatus') {
      // This is a status characteristic - process as before
      return await this.processStatusCharacteristic(characteristic);
    } else if (characteristic.protobufType === 'ProtobufCollectionRecord') {
      // This is a record characteristic - skip (should be handled by status)
      return null;
    } else {
      // This is a collection with the actual record type (e.g., "MapPoint")
      return await this.processCollectionWithRecordType(characteristic);
    }
  }

  // Helper function to process status characteristics
  async processStatusCharacteristic(characteristic) {
    const statusUuid = characteristic.uuid;
    const recordUuid = statusUuid + 1; // Record UUID is status UUID + 1

    try {
      // Test status characteristic
      const statusCharacteristic = await this.protobufService.getCharacteristic(statusUuid);
      
      const processedCollection = {
        uuid: statusUuid,
        protobufType: characteristic.protobufType,
        messageType: this.protobufSchemaProtocol.lookupType('ProtobufCollectionStatus'),
        BLEcharacteristic: statusCharacteristic,
        value: null, // Collections don't use this value field
        writable: characteristic.writable,
        autoUpdate: true,
        hasChanges: false,
        isCollection: true,
        collectionData: {
          statusUuid: statusUuid,
          recordUuid: recordUuid,
          recordProtobufType: 'MapPoint' // Based on Arduino addCollection("MapPoint", ...)
        }
      };

      return processedCollection;
    } catch (error) {
      console.error(`Failed to get characteristic: ${error.message}`);
      return null;
    }
  }

  // Helper function to process collection with actual record type
  async processCollectionWithRecordType(characteristic) {
    const recordType = characteristic.protobufType; // e.g., "MapPoint"
    const statusUuid = characteristic.uuid;
    const recordUuid = statusUuid + 1; // Record UUID is status UUID + 1
    
    try {
      const statusCharacteristic = await this.protobufService.getCharacteristic(statusUuid);
      
      const processedCollection = {
        uuid: statusUuid,
        protobufType: 'ProtobufCollectionStatus', // Use the collection protocol type
        messageType: this.protobufSchemaProtocol.lookupType('ProtobufCollectionStatus'),
        BLEcharacteristic: statusCharacteristic,
        value: null, // Collections don't use this value field
        writable: characteristic.writable,
        autoUpdate: true,
        hasChanges: false,
        isCollection: true,
        collectionData: {
          statusUuid: statusUuid,
          recordUuid: recordUuid,
          recordProtobufType: recordType // Use the actual record type from the characteristic
        }
      };

      return processedCollection;
    } catch (error) {
      console.error(`Failed to get status characteristic: ${error.message}`);
      return null;
    }
  }

  // Characteristic value management
  updateCharacteristicValue(index, key, newValue) {
    if (index >= 0 && index < this.protobufCharacteristics.length) {
      const characteristic = this.protobufCharacteristics[index];

      // Update the specific field within the characteristic's value
      characteristic.value[key] = newValue;
      characteristic.hasChanges = true;

      if (characteristic.autoUpdate) {
        if (this.characteristicUpdateTimeouts.has(index)) { 
          clearTimeout(this.characteristicUpdateTimeouts.get(index));
        }
        const timeout = setTimeout(() => this.pushChanges(index), AUTO_UPDATE_TIMEOUT);
        this.characteristicUpdateTimeouts.set(index, timeout);
      }

      // Emit change event
      this.emit('characteristicChanged', {
        index,
        uuid: characteristic.uuid,
        key,
        newValue,
        hasChanges: characteristic.hasChanges
      });
    }
  }

  async pushChanges(index) {
    if (index >= 0 && index < this.protobufCharacteristics.length) {
      const characteristic = this.protobufCharacteristics[index];
      const encodedValue = characteristic.messageType.encode(characteristic.value).finish();
      await characteristic.BLEcharacteristic.writeValue(encodedValue);

      characteristic.hasChanges = false;
      this.characteristicUpdateTimeouts.delete(index);

      // Emit update event
      this.emit('characteristicPushed', {
        index,
        uuid: characteristic.uuid,
        hasChanges: false
      });
    }
  }

  setAutoUpdate(index, autoUpdate) {
    if (index >= 0 && index < this.protobufCharacteristics.length) {
      this.protobufCharacteristics[index].autoUpdate = autoUpdate;
      
      // Emit autoUpdate change event
      this.emit('autoUpdateChanged', {
        index,
        uuid: this.protobufCharacteristics[index].uuid,
        autoUpdate
      });
    }
  }

  // Schema generation
  generateSchemaJson() {
    if (!this.protobufSchema || !this.protobufCharacteristics.length) {
      return null;
    }

    const schemaJson = {
      deviceSchema: {
        serviceUuid: UUIDs.protobufService,
        schemaVersion: this.protobufSchema.version || 'unknown',
        schemaDefinition: this.protobufSchema.schema || '',
        characteristics: []
      }
    };

    // Add characteristics from the original schema
    for (const characteristic of this.protobufSchema.characteristics) {
      const charJson = {
        uuid: characteristic.uuid,
        protobufType: characteristic.protobufType,
        writable: characteristic.writable || false,
        isCollection: this.isCollectionCharacteristic(characteristic),
        properties: {
          read: false,
          write: false,
          notify: false,
          indicate: false
        }
      };

      // Find the corresponding processed characteristic to get BLE properties
      const processedChar = this.protobufCharacteristics.find(c => c.uuid === characteristic.uuid);
      if (processedChar && processedChar.BLEcharacteristic) {
        const bleProps = processedChar.BLEcharacteristic.properties;
        charJson.properties = {
          read: bleProps.read || false,
          write: bleProps.write || false,
          writeWithoutResponse: bleProps.writeWithoutResponse || false,
          notify: bleProps.notify || false,
          indicate: bleProps.indicate || false,
          authenticatedSignedWrites: bleProps.authenticatedSignedWrites || false,
          reliableWrite: bleProps.reliableWrite || false,
          writableAuxiliaries: bleProps.writableAuxiliaries || false
        };
      }

      // Add collection-specific data if it's a collection
      if (charJson.isCollection) {
        if (processedChar && processedChar.collectionData) {
          charJson.collectionData = {
            statusUuid: processedChar.collectionData.statusUuid,
            recordUuid: processedChar.collectionData.recordUuid,
            recordProtobufType: processedChar.collectionData.recordProtobufType
          };
        }
      }

      schemaJson.deviceSchema.characteristics.push(charJson);
    }

    return schemaJson;
  }

  // Getters
  getConnectionState() {
    return {
      isConnected: this.isConnected,
      connectionLost: this.connectionLost
    };
  }

  getProtobufSchema() {
    return this.protobufSchema;
  }

  getProtobufCharacteristics() {
    return this.protobufCharacteristics;
  }

  getProtobufSchemaRoot() {
    return this.protobufSchemaRoot;
  }

  getProtobufSchemaProtocol() {
    return this.protobufSchemaProtocol;
  }

  getProtobufRoot() {
    return this.protobufRoot;
  }

  async getCharacteristic(uuid) {
    if (!this.protobufService) {
      throw new Error('Protobuf service not initialized');
    }
    return await this.protobufService.getCharacteristic(uuid);
  }

  // Every characteristic the service publishes under `uuid`, plus the one the singular
  // getter binds. Usually these are the same single characteristic. The compass, however,
  // publishes each collection *status* uuid twice — addCollection() puts it in the schema
  // (protobufBLEService.cpp:453), the schema loop builds a characteristic for it, and a
  // second loop builds another at the same uuid and keeps only that one (:225). Writes
  // reach the device on either copy because it dispatches by uuid value (:267), but replies
  // leave only the kept copy and only while *that* copy is subscribed (:371). So we must
  // startNotifications() on all of them or the device may never transmit at all.
  //
  // The promise is cached, not the result, so concurrent callers share one lookup.
  getAllCharacteristics(uuid) {
    if (!this.protobufService) {
      return Promise.reject(new Error('Protobuf service not initialized'));
    }
    if (!this.characteristicCopies.has(uuid)) {
      const pending = this.resolveAllCharacteristics(uuid);
      pending.catch(() => this.characteristicCopies.delete(uuid));
      this.characteristicCopies.set(uuid, pending);
    }
    return this.characteristicCopies.get(uuid);
  }

  async resolveAllCharacteristics(uuid) {
    const bound = await this.protobufService.getCharacteristic(uuid);
    let all = null;
    try {
      all = await this.protobufService.getCharacteristics(uuid);
    } catch (err) {
      console.warn(`getCharacteristics(${uuid}) unavailable, falling back to the single getter`, err);
    }
    if (!all || all.length === 0) {
      all = [bound];
    }
    all = Array.from(new Set(all)); // never attach twice to the same object
    console.log(
      `Characteristic ${uuid}: device publishes ${all.length} copy/copies; ` +
      `writes go to index ${all.indexOf(bound)}`
    );
    return { all, bound };
  }

  // Replace any listener already registered on this exact characteristic object, so a
  // repeated subscribe cannot strand one where disconnect() can no longer reach it.
  attachNotificationListener(characteristic, listener) {
    const previous = this.notificationListeners.get(characteristic);
    if (previous) {
      characteristic.removeEventListener('characteristicvaluechanged', previous);
    }
    characteristic.addEventListener('characteristicvaluechanged', listener);
    this.notificationListeners.set(characteristic, listener);
  }

  // Subscribe every copy of `uuid` that can notify. Returns the copy the singular getter
  // binds, which is what this function used to return. Throws only if every notify-capable
  // copy refused, which is the same failure the single-copy version had.
  async subscribeAllCopies(uuid, label, makeListener) {
    const { all, bound } = await this.getAllCharacteristics(uuid);
    let notifyCapable = 0;
    let subscribed = 0;
    let firstError = null;

    for (let i = 0; i < all.length; i++) {
      const characteristic = all[i];
      if (!characteristic.properties.notify) continue;
      notifyCapable++;
      try {
        await characteristic.startNotifications();
      } catch (err) {
        // A dead duplicate must never stop us binding the live one.
        if (!firstError) firstError = err;
        console.warn(`Copy ${i} of ${label} ${uuid} would not subscribe`, err);
        continue;
      }
      this.attachNotificationListener(characteristic, makeListener());
      subscribed++;
    }

    console.log(`Subscribed to ${subscribed}/${notifyCapable} ${label} copies of ${uuid}`);
    if (notifyCapable > 0 && subscribed === 0) {
      throw firstError;
    }
    return bound;
  }

  // Collection-specific methods
  async subscribeToCollectionStatus(statusUuid, callback) {
    return await this.subscribeAllCopies(statusUuid, 'status', () => (event) => {
      try {
        const buffer = new Uint8Array(event.target.value.buffer);
        const StatusType = this.protobufSchemaProtocol.lookupType('ProtobufCollectionStatus');
        const status = StatusType.decode(buffer);
        //console.log('status decoded', statusUuid, status);
        callback(status);
      } catch (err) {
        console.error('Error handling collection status change:', err);
      }
    });
  }

  async subscribeToCollectionRecord(recordUuid, callback) {
    // Record uuids are not duplicated today — addCollection only schemas the status uuid
    // (protobufBLEService.cpp:453) — so this is normally a one-element loop. Same shape
    // anyway, so a future firmware that duplicates them needs no client change.
    return await this.subscribeAllCopies(recordUuid, 'record', () => (event) => {
      try {
        const buffer = new Uint8Array(event.target.value.buffer);
        const RecordType = this.protobufSchemaProtocol.lookupType('ProtobufCollectionRecord');
        const record = RecordType.decode(buffer);
        callback(record);
      } catch (err) {
        console.error('Error handling collection record change:', err);
      }
    });
  }

  async writeCollectionStatus(statusUuid, statusData) {
    if (!this.protobufService) {
      throw new Error('Protobuf service not initialized');
    }
    
    const statusCharacteristic = await this.protobufService.getCharacteristic(statusUuid);
    const StatusType = this.protobufSchemaProtocol.lookupType('ProtobufCollectionStatus');
    const buffer = StatusType.encode(statusData).finish();

    for (let i = 0; i < RETRY_COUNT; i++) {
      try {
        //console.log(`Writing status characteristic ${statusUuid} (attempt ${i + 1}/${RETRY_COUNT})`);
        await statusCharacteristic.writeValue(buffer);
        //console.log(`Status characteristic ${statusUuid} written successfully`);
        return; // Success, exit retry loop
      } catch (error) {
        const delay = Math.floor(Math.random() * (MAX_RETRY_DELAY - MIN_RETRY_DELAY + 1)) + MIN_RETRY_DELAY;
        console.warn(`Write failed, retrying in ${delay}ms (attempt ${i + 1}/${RETRY_COUNT})`, error);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    console.error(`Failed to write status characteristic after ${RETRY_COUNT} retries.`);
  }

  async writeCollectionRecord(recordUuid, recordData) {
    if (!this.protobufService) {
      throw new Error('Protobuf service not initialized');
    }
    
    const recordCharacteristic = await this.protobufService.getCharacteristic(recordUuid);
    const RecordType = this.protobufSchemaProtocol.lookupType('ProtobufCollectionRecord');
    const buffer = RecordType.encode(recordData).finish();

    for (let i = 0; i < RETRY_COUNT; i++) {
      try {
        console.log(`Writing record characteristic ${recordUuid} (attempt ${i + 1}/${RETRY_COUNT})`,recordData);
        await recordCharacteristic.writeValue(buffer);
        return; // Success, exit retry loop
      } catch (error) {
        const delay = Math.floor(Math.random() * (MAX_RETRY_DELAY - MIN_RETRY_DELAY + 1)) + MIN_RETRY_DELAY;
        console.warn(`Write failed, retrying in ${delay}ms (attempt ${i + 1}/${RETRY_COUNT})`, error);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    console.error(`Failed to write record characteristic after ${RETRY_COUNT} retries.`);
  }

  async writeCollectionStatusAndWaitForResult(statusUuid, statusData, timeoutMs = 5000) {
    return new Promise((resolve, reject) => {
      this._writeStatusQueue.push({ statusUuid, statusData, timeoutMs, resolve, reject });
      this._processWriteStatusQueue();
    });
  }

  async _processWriteStatusQueue() {
    if (this._isProcessingWriteStatus) return;
    const next = this._writeStatusQueue.shift();
    if (!next) return;
    this._isProcessingWriteStatus = true;
    try {
      // Subscribe to the next status notification (one-shot)
      const statusPromise = this._waitForNextCollectionStatus(next.statusUuid, next.timeoutMs);
      await this.writeCollectionStatus(next.statusUuid, next.statusData);
      const status = await statusPromise;
      next.resolve(status);
    } catch (err) {
      next.reject(err);
    } finally {
      this._isProcessingWriteStatus = false;
      if (this._writeStatusQueue.length > 0) {
        this._processWriteStatusQueue();
      }
    }
  }

  _waitForNextCollectionStatus(statusUuid, timeoutMs = 20000) {
    return new Promise(async (resolve, reject) => {
      // Listen on every copy. subscribeToCollectionStatus turned notifications on for all
      // of them; this one-shot waiter has to watch all of them too, or it would still miss
      // the reply that arrives on a copy it is not holding.
      let copies;
      try {
        copies = (await this.getAllCharacteristics(statusUuid)).all;
      } catch (err) {
        console.error('Error getting characteristic:', err);
        reject(err);
        return;
      }
      let timeout = null;
      const cleanup = () => {
        clearTimeout(timeout);
        for (const characteristic of copies) {
          characteristic.removeEventListener('characteristicvaluechanged', handler);
        }
      };
      const handler = (event) => {
        try {
          const buffer = new Uint8Array(event.target.value.buffer);
          const StatusType = this.protobufSchemaProtocol.lookupType('ProtobufCollectionStatus');
          const status = StatusType.decode(buffer);
          cleanup();
          resolve(status);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
      for (const characteristic of copies) {
        characteristic.addEventListener('characteristicvaluechanged', handler);
      }
      timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout waiting for collection status notification'));
      }, timeoutMs);
    });
  }

  // Event system
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }
}

// Create and export a singleton instance
const protobufProtocolService = new ProtobufProtocolService();
export default protobufProtocolService; 